import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Activity,
  Clock,
  Users,
  Key,
  LogIn,
  LogOut,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Brain,
  Sparkles,
  ChevronDown,
  Database,
  Eye,
} from "lucide-react";
import { SecureMarkdown } from "@/components/SecureMarkdown";

// Markdown styling matching InsightsPage
const mdClasses = `prose prose-invert prose-sm max-w-none p-3
  [&_p]:text-sm [&_p]:text-foreground/85 [&_p]:leading-relaxed
  [&_ul]:text-sm [&_ul]:text-foreground/80
  [&_li]:mb-1 [&_strong]:text-foreground`;

// Event configurations
const EVENT_CONFIG = {
  login_success: { icon: LogIn, color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", label: "Login" },
  login_failure: { icon: ShieldAlert, color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20", label: "Failed Login" },
  logout: { icon: LogOut, color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", label: "Logout" },
  token_refresh: { icon: RefreshCw, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20", label: "Token Refresh" },
  token_revoked: { icon: Key, color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", label: "Token Revoked" },
  member_invited: { icon: Users, color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", label: "Member Invited" },
  member_removed: { icon: Users, color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", label: "Member Removed" },
  credential_created: { icon: Key, color: "text-cyan-500", bg: "bg-cyan-500/10", border: "border-cyan-500/20", label: "Credential Added" },
  environment_created: { icon: Shield, color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", label: "Environment Created" },
  default: { icon: Activity, color: "text-muted-foreground", bg: "bg-muted/10", border: "border-muted/20", label: "Event" },
};

const getEventConfig = (eventType) => EVENT_CONFIG[eventType] || EVENT_CONFIG.default;

// Health score styling
const getScoreConfig = (score) => {
  if (score >= 90) return { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", grade: "A" };
  if (score >= 80) return { color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", grade: "B" };
  if (score >= 70) return { color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", grade: "C" };
  if (score >= 60) return { color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", grade: "D" };
  return { color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20", grade: "F" };
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// Collapsible Section (matching InsightsPage pattern)
function AuditSection({ title, icon: Icon, iconColor, children, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] hover:border-[#2a2a30] transition-colors">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-medium">{title}</span>
          {badge && <Badge variant="outline" className="text-[10px] ml-2">{badge}</Badge>}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="pl-2 border-l-2 border-[var(--n-border-2)] ml-2">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Event Row Component
function EventRow({ event }) {
  const config = getEventConfig(event.event_type);
  const Icon = config.icon;
  const isFailure = event.result === "failure" || event.result === "error";

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bg} border ${config.border}`}>
      <div className={`p-2 rounded-lg bg-[var(--n-bg)]`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{config.label}</span>
          {isFailure && (
            <Badge variant="outline" className="text-[10px] border-red-500/20 text-red-500">Failed</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {event.actor?.email || event.actor?.user_id || "System"}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-muted-foreground">{formatRelativeTime(event.timestamp)}</p>
        {isFailure ? (
          <XCircle className="w-3.5 h-3.5 text-red-500 ml-auto mt-1" />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto mt-1" />
        )}
      </div>
    </div>
  );
}

/**
 * 3-Tier Audit System
 * 
 * Tier 1 & 2: Control Plane (niyant_control)
 * Tier 3: Data Plane (niyant_data.env_{id}) - shown in EnvironmentsTab
 */
export default function AuditSystem({ api }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [healthScore, setHealthScore] = useState(null);
  const [summary, setSummary] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [healthRes, summaryRes, recentRes, timelineRes] = await Promise.all([
        api().get("/audit/health-score").catch(() => ({ data: { score: 100, grade: "A", factors: [], trend: "stable" } })),
        api().get("/audit/summary").catch(() => ({ data: {} })),
        api().get("/audit/recent?limit=20").catch(() => ({ data: { events: [] } })),
        api().get("/audit/timeline?hours=24").catch(() => ({ data: { timeline: [] } })),
      ]);

      setHealthScore(healthRes.data);
      setSummary(summaryRes.data);
      setRecentEvents(recentRes.data.events || []);
      setTimeline(timelineRes.data.timeline || []);
    } catch (err) {
      console.error("Failed to load audit data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  const fetchBriefing = useCallback(async () => {
    setBriefingLoading(true);
    try {
      const res = await api().get("/audit/ai-briefing");
      setBriefing(res.data);
    } catch (err) {
      console.error("Failed to load AI briefing:", err);
      setBriefing({ summary: "Unable to generate AI briefing at this time.", key_insights: [], recommendations: [] });
    } finally {
      setBriefingLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    fetchBriefing();
  }, [fetchData, fetchBriefing]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardContent className="p-6"><div className="h-24 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const scoreConfig = getScoreConfig(healthScore?.score || 100);
  const totalEvents = summary?.tier1_platform?.total || 0;
  const eventsToday = summary?.tier1_platform?.today || 0;

  return (
    <div className="space-y-6" data-testid="audit-system">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
            Security Audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform security, authentication, and compliance monitoring
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="border-[var(--n-border-2)]"
          data-testid="refresh-audit"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[var(--n-card)] border border-[var(--n-border-2)] h-9">
          <TabsTrigger
            value="overview"
            className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]"
            data-testid="tab-overview"
          >
            <Brain className="w-3.5 h-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger
            value="platform"
            className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]"
            data-testid="tab-platform"
          >
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Platform (Tier 1)
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]"
            data-testid="tab-activity"
          >
            <Activity className="w-3.5 h-3.5 mr-1.5" /> Activity (Tier 2)
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-0">
          {/* Health Score Card */}
          <Card className={`bg-[var(--n-card)] border ${scoreConfig.border}`} data-testid="health-score-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <ShieldCheck className={`w-4 h-4 ${scoreConfig.color}`} />
                  Security Health Score
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Database className="w-3 h-3" /> {totalEvents.toLocaleString()} events
                  </span>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {eventsToday} today
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <div className={`text-5xl font-bold ${scoreConfig.color}`}>
                  {healthScore?.score || 100}
                </div>
                <div>
                  <Badge variant="outline" className={`${scoreConfig.border} ${scoreConfig.color} text-sm px-3 py-1`}>
                    Grade {scoreConfig.grade}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-2">
                    Based on {summary?.tier1_platform?.this_week || 0} events this week
                  </p>
                </div>
              </div>

              {/* Health Factors */}
              <div className="space-y-2">
                {(healthScore?.factors || []).map((factor, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {factor.status === "good" ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : factor.impact < 0 ? (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="text-foreground/80">{factor.name}</span>
                    {factor.detail && (
                      <span className="text-muted-foreground text-xs">— {factor.detail}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Security Briefing */}
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]" data-testid="ai-briefing-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  AI Security Briefing
                </CardTitle>
                {briefingLoading && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {briefing ? (
                <>
                  <p className="text-sm text-foreground/85 leading-relaxed">
                    {briefing.summary || "No summary available."}
                  </p>

                  {briefing.key_insights?.length > 0 && (
                    <AuditSection title="Key Insights" icon={Eye} iconColor="text-[var(--hig-accent)]" defaultOpen>
                      <ul className="space-y-1 py-2">
                        {briefing.key_insights.map((insight, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                            <span className="text-[var(--hig-accent)]">•</span>
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </AuditSection>
                  )}

                  {briefing.anomalies?.length > 0 && (
                    <AuditSection title="Anomalies Detected" icon={AlertTriangle} iconColor="text-amber-500" badge={briefing.anomalies.length}>
                      <div className="space-y-2 py-2">
                        {briefing.anomalies.map((anomaly, i) => (
                          <div key={i} className={`p-2 rounded-lg ${
                            anomaly.risk === "HIGH" ? "bg-red-500/10 border border-red-500/20" :
                            anomaly.risk === "MEDIUM" ? "bg-amber-500/10 border border-amber-500/20" :
                            "bg-slate-500/10 border border-slate-500/20"
                          }`}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{anomaly.title}</span>
                              <Badge variant="outline" className={`text-[10px] ${
                                anomaly.risk === "HIGH" ? "border-red-500/20 text-red-500" :
                                anomaly.risk === "MEDIUM" ? "border-amber-500/20 text-amber-500" :
                                "border-slate-500/20 text-slate-400"
                              }`}>{anomaly.risk}</Badge>
                            </div>
                            {anomaly.detail && (
                              <p className="text-xs text-muted-foreground mt-1">{anomaly.detail}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </AuditSection>
                  )}

                  {briefing.recommendations?.length > 0 && (
                    <AuditSection title="Recommendations" icon={ShieldCheck} iconColor="text-emerald-500">
                      <ul className="space-y-1 py-2">
                        {briefing.recommendations.map((rec, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                            <span className="text-emerald-500">→</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </AuditSection>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Generating briefing...</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          {timeline.length > 0 && (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[var(--hig-accent)]" />
                  Activity (Last 24 Hours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-24">
                  {timeline.map((hour, i) => {
                    const maxCount = Math.max(...timeline.map(h => h.total), 1);
                    const height = (hour.total / maxCount) * 100;
                    const hasFailures = hour.failures > 0;

                    return (
                      <div
                        key={i}
                        className="flex-1 flex flex-col items-center group"
                        title={`${hour.hour}: ${hour.total} events`}
                      >
                        <div
                          className={`w-full rounded-t transition-all ${
                            hasFailures ? "bg-red-500/60" : "bg-[var(--hig-accent)]/40"
                          } hover:bg-[var(--hig-accent)]`}
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        {i % 4 === 0 && (
                          <span className="text-[10px] text-muted-foreground mt-1">
                            {hour.hour?.slice(-2)}h
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Events */}
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4 text-[var(--hig-accent)]" />
                Recent Events
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {recentEvents.length === 0 ? (
                <div className="py-8 text-center">
                  <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No recent events</p>
                </div>
              ) : (
                recentEvents.slice(0, 10).map((event, i) => (
                  <EventRow key={event.id || i} event={event} />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform (Tier 1) Tab */}
        <TabsContent value="platform" className="space-y-4 mt-0">
          <PlatformAuditTab api={api} />
        </TabsContent>

        {/* Activity (Tier 2) Tab */}
        <TabsContent value="activity" className="space-y-4 mt-0">
          <ActivityAuditTab api={api} summary={summary} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Platform Audit Tab (Tier 1 - Control Plane)
function PlatformAuditTab({ api }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchPlatformEvents = async () => {
      try {
        const res = await api().get("/audit/platform?limit=50");
        setEvents(res.data.events || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        console.error("Failed to load platform events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlatformEvents();
  }, [api]);

  if (loading) {
    return (
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardContent className="p-6"><div className="h-32 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4 text-[var(--hig-accent)]" />
            Platform Audit
            <Badge variant="outline" className="text-[10px] ml-2">Tier 1</Badge>
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {total.toLocaleString()} events • 7 year retention • Control Plane
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {events.length === 0 ? (
          <div className="py-8 text-center">
            <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No platform events</p>
          </div>
        ) : (
          events.map((event, i) => <EventRow key={event.id || i} event={event} />)
        )}
      </CardContent>
    </Card>
  );
}

// Activity Audit Tab (Tier 2 - Control Plane)
function ActivityAuditTab({ api, summary }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivityEvents = async () => {
      try {
        const res = await api().get("/audit/activity?limit=50");
        setEvents(res.data.events || []);
      } catch (err) {
        console.error("Failed to load activity events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivityEvents();
  }, [api]);

  const envActivity = summary?.tier2_activity?.by_environment || {};

  return (
    <div className="space-y-4">
      {/* Environment Breakdown */}
      {Object.keys(envActivity).length > 0 && (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="w-4 h-4 text-[var(--hig-accent)]" />
              Activity by Environment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(envActivity).map(([envId, count]) => (
                <div key={envId} className="flex items-center justify-between p-2 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)]">
                  <span className="text-sm text-foreground/80 truncate">{envId}</span>
                  <Badge variant="outline" className="text-[10px]">{count} events</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Events */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              Activity Digest
              <Badge variant="outline" className="text-[10px] ml-2">Tier 2</Badge>
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              3 year retention • Control Plane
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-32 bg-[var(--n-border-2)] rounded animate-pulse" />
          ) : events.length === 0 ? (
            <div className="py-8 text-center">
              <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-sm font-medium">No activity events yet</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Activity events capture usage metrics and billing data
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event, i) => <EventRow key={event.id || i} event={event} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
